import { computed, ref } from 'vue'
import { createInjectionState, toReactive } from '@vueuse/core'
import {
  ToQueryStringFilters,
  availableCatalogs,
  useOrderByFilter,
  usePagination,
  useToQueryString,
} from '@/core'
import type { TArticle } from '../../../types'
import { useModuleQuery } from '../query/useModuleQuery'
import { Pagination, TRecordAction } from '@/core/types'
import { useCatalogStore } from '@/core/store/catalogs'
import { storeToRefs } from 'pinia'

const [useProvideModule, useModule] = createInjectionState(() => {
  const rows = ref<number[]>([])
  const form = ref<TArticle>({})
  const filters = ref<ToQueryStringFilters>({
    param: '',
    dates: {
      created_at: [],
    },
    order_by: 'status_id,asc',
  })

  const store = useCatalogStore()
  const { catalogs } = storeToRefs(store)

  const moduleQueryParams = useToQueryString(toReactive(filters), {
    searchColumns: [
      'galvanizedGrade.name',
      'hardness.name',
      'gauge.name',
      'type.name',
      'measurementUnit.name',
      'status.name',
    ],
  })

  const modulePagination = usePagination()

  const moduleQuery = useModuleQuery({
    currentPage: modulePagination.currentPage,
    filters: {
      loadRecords: moduleQueryParams.queryParams,
    },
    enableQuery: {
      categoriesCatalog: true,
    },
    handlers: {
      onDataChange: (pagination: Pagination) => {
        modulePagination.setPagination(pagination)
      },
    },
  })

  const { orderByOptions } = useOrderByFilter({
    columns: ['created_at'],
  })

  const onAdd = async (payload: TArticle) => {
    await moduleQuery.addRecord.mutateAsync({
      ...payload,
      color: payload.color_type_id === 24 ? payload.color : '',
      color_abbreviation:
        payload.color_type_id === 24 ? payload.color_abbreviation : '',
    })
  }

  const onEdit = async (payload: TArticle) => {
    await moduleQuery.editRecord.mutateAsync({
      ...payload,
      color: payload.color_type_id === 24 ? payload.color : '',
      color_abbreviation:
        payload.color_type_id === 24 ? payload.color_abbreviation : '',
    })
  }

  const onAction = async (action: TRecordAction) => {
    await moduleQuery.actionRecord.mutateAsync(action)
  }

  const onRequestCatalog = async (payload: number[]) => {
    await moduleQuery.requestCatalog.mutateAsync({ ids: payload })
  }

  const tableData = computed(() => moduleQuery.loadRecords.data?.value?.data)
  const isLoading = computed(
    () =>
      moduleQuery.addRecord.isLoading.value ||
      moduleQuery.editRecord.isLoading.value ||
      moduleQuery.loadRecords.isLoading.value,
  )
  const isLoadingCategories = computed(
    () => moduleQuery.requestCatalog.isLoading.value,
  )

  const categoriesTypes = computed(() =>
    catalogs.value.types.filter(
      (c) => c.id !== availableCatalogs.finished_matter,
    ),
  )

  return {
    tableData,
    filters,
    orderByOptions,
    rows,
    form,
    isLoading,
    modulePagination: toReactive(modulePagination),
    isLoadingCategories,
    catalogs,
    categoriesTypes,
    onAction,
    onEdit,
    onAdd,
    onRequestCatalog,
  }
})

export { useProvideModule, useModule }
